/**
 * Buy X for Y bundle: per-category color + options, multi-line-item cart add.
 * Used with <product-bundler-xfy> from bundle-selector-xfy.liquid.
 */
(() => {
  class ProductBundlerXfy extends HTMLElement {
    #mediaAbortController;
    #boundStickyClick;

    connectedCallback() {
      this.addToCartButton = this.querySelector('[data-bundle-add]');
      this.status = this.querySelector('[data-bundle-status]');
      this.form = this.querySelector('[data-bundle-form]');
      this.sectionRoot = this.closest('.shopify-section') || this.closest('product-bundler-xfy');
      this.mediaGrid = this.sectionRoot?.querySelector('[data-bundle-media-grid]') || null;

      if (!this.form || !this.addToCartButton) return;

      this.bundleProducts = this.#readBundleProducts();
      this.xfyCategories = this.#readBundleCategories();
      this.xfyState = Object.create(null);

      this.xfyCategories.forEach((cat) => {
        const key = String(cat.key);
        const input = this.querySelector(`input[name="xfy-color-${key}"]:checked`);
        const pid = input ? Number(input.value) : Number(cat.initialProductId);
        this.xfyState[key] = { selectedProductId: pid, selectedOptions: Object.create(null) };
      });

      this.#boundStickyClick = (e) => {
        if (!e.target.closest('[data-bundle-sticky-add]')) return;
        if (this.addToCartButton && !this.addToCartButton.disabled) {
          this.addToCartButton.click();
        }
      };
      document.addEventListener('click', this.#boundStickyClick);

      this.form.addEventListener('change', (e) => this.#xfyOnFormChange(e));
      this.form.addEventListener('submit', (e) => this.#xfyOnSubmit(e));

      this.xfyCategories.forEach((cat) => {
        this.#xfyRenderOptionPanel(String(cat.key));
      });
      this.#xfyUpdateColorLabels();
      this.#xfySyncCategoryImages();
      if (this.xfyCategories[0]) {
        this.#xfyUpdateMediaPreviewForKey(String(this.xfyCategories[0].key));
      }
      this.#xfyUpdateBundlePricing();
      this.#xfySyncUi();
    }

    disconnectedCallback() {
      if (this.#boundStickyClick) {
        document.removeEventListener('click', this.#boundStickyClick);
      }
    }

    #readBundleProducts() {
      const source = this.querySelector('[data-bundle-products-json]');
      if (!source) return [];
      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Bundle JSON parsing failed', error);
        return [];
      }
    }

    #readBundleCategories() {
      const source = this.querySelector('[data-bundle-categories-json]');
      if (!source) return [];
      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Bundle categories JSON parsing failed', error);
        return [];
      }
    }

    #findBundleProduct(productId) {
      return this.bundleProducts.find((item) => Number(item.id) === Number(productId)) || null;
    }

    #renderSimpleMediaGrid(media, fallbackTitle = '') {
      if (!this.mediaGrid) return;
      const mediaList = Array.isArray(media) ? media : [];
      if (!mediaList.length) return;

      this.mediaGrid.innerHTML = '';

      mediaList.slice(0, 4).forEach((item) => {
        const mediaItem = document.createElement('div');
        mediaItem.className = 'product-bundler__media-item';

        const image = document.createElement('img');
        image.src = item.src;
        image.alt = item.alt || fallbackTitle;
        image.loading = 'lazy';

        mediaItem.appendChild(image);
        this.mediaGrid.appendChild(mediaItem);
      });
    }

    async #updateMediaGallery(productId, sizeValue = '') {
      const selectedProduct = this.#findBundleProduct(productId);
      if (!selectedProduct) return;

      const media = Array.isArray(selectedProduct.media) ? selectedProduct.media : [];
      if (!media.length) return;

      if (this.mediaGrid) {
        this.#renderSimpleMediaGrid(media, selectedProduct.title || '');
        return;
      }

      const section = this.closest('.shopify-section');
      const currentGallery = section?.querySelector('media-gallery');
      if (!currentGallery || !selectedProduct.url) return;

      const variant = this.#findVariant(productId, sizeValue);
      const requestUrl = new URL(selectedProduct.url, window.location.origin);
      if (variant?.id) requestUrl.searchParams.set('variant', String(variant.id));

      try {
        this.#mediaAbortController?.abort();
        this.#mediaAbortController = new AbortController();

        const response = await fetch(requestUrl.toString(), { signal: this.#mediaAbortController.signal });
        if (!response.ok) return;

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const nextGallery = html.querySelector('media-gallery');
        if (nextGallery) currentGallery.replaceWith(nextGallery);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Bundle gallery update failed', error);
        }
      }
    }

    #findVariant(productId, sizeValue) {
      const selectedProduct = this.#findBundleProduct(productId);
      if (!selectedProduct) return null;

      const optionIndex = Number(selectedProduct.size_option_index);
      const variants = Array.isArray(selectedProduct.variants) ? selectedProduct.variants : [];

      if (!variants.length) return null;

      if (optionIndex < 0 || !sizeValue) {
        return variants.find((variant) => variant.available) || null;
      }

      return (
        variants.find((variant) => {
          const optionValue = Array.isArray(variant.options) ? variant.options[optionIndex] : null;
          return optionValue === sizeValue && variant.available;
        }) || null
      );
    }

    #setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('bundle-form__status--error', isError);
      this.status.classList.toggle('bundle-selector__status--error', isError);
    }

    #getStickyButton() {
      return document.querySelector('[data-bundle-sticky-add]');
    }

    #xfyProductForCategory(cat, productId) {
      return cat.products?.find((p) => Number(p.id) === Number(productId)) || null;
    }

    #xfySelectedProductForKey(key) {
      const cat = this.xfyCategories.find((c) => String(c.key) === String(key));
      if (!cat) return null;
      const st = this.xfyState[String(key)];
      return this.#xfyProductForCategory(cat, st?.selectedProductId);
    }

    #xfyEscapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
    }

    #xfyEscapeAttr(s) {
      return String(s).replace(/\\/g, '\\\\').replace(/"/g, '&quot;');
    }

    #xfyOptionValueAvailable(product, optionIndex, value, selectedOptions) {
      const owv = product.options_with_values || [];
      const want = String(value).trim();
      return (product.variants || []).some((variant) => {
        if (!variant.available) return false;
        if (String(variant.options[optionIndex]).trim() !== want) return false;
        return owv.every((opt, idx) => {
          if (idx === optionIndex) return true;
          const k = opt.name.toLowerCase();
          const sel = selectedOptions[k];
          if (!sel) return true;
          const v = variant.options[idx];
          return v && String(sel).toLowerCase().trim() === String(v).toLowerCase().trim();
        });
      });
    }

    #xfyMatchingVariant(product, selectedOptions) {
      if (!product) return null;
      const owv = product.options_with_values || [];
      const variants = product.variants || [];
      if (!owv.length) return variants.find((v) => v.available) || null;
      return (
        variants.find((variant) => {
          if (!variant.available) return false;
          return owv.every((opt, idx) => {
            const k = opt.name.toLowerCase();
            const sel = selectedOptions[k];
            if (!sel) return false;
            const v = variant.options[idx];
            return v && String(sel).toLowerCase().trim() === String(v).toLowerCase().trim();
          });
        }) || null
      );
    }

    #xfyPartialVariantForGallery(product, selectedOptions) {
      if (!product) return null;
      const owv = product.options_with_values || [];
      const variants = product.variants || [];
      if (!owv.length) return variants.find((v) => v.available) || null;
      return (
        variants.find((variant) => {
          if (!variant.available) return false;
          return owv.every((opt, idx) => {
            const k = opt.name.toLowerCase();
            const sel = selectedOptions[k];
            if (!sel) return true;
            const v = variant.options[idx];
            return v && String(sel).toLowerCase().trim() === String(v).toLowerCase().trim();
          });
        }) || variants.find((v) => v.available) || null
      );
    }

    /** Resolved variant for pricing UI: full selection, else best partial / first available. */
    #xfyVariantForPricing(product, selectedOptions) {
      if (!product) return null;
      const full = this.#xfyMatchingVariant(product, selectedOptions);
      if (full) return full;
      return this.#xfyPartialVariantForGallery(product, selectedOptions || {});
    }

    #xfyFormatMoneyCents(cents) {
      const n = Math.round(Number(cents));
      if (!Number.isFinite(n)) return '';
      const amount = n / 100;
      const hasFraction = n % 100 !== 0;
      const shopSymbol = (this.dataset.xfyCurrencySymbol || '').trim();

      const formatDecimal = () => {
        try {
          return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: hasFraction ? 2 : 0,
            maximumFractionDigits: 2,
            useGrouping: true,
          }).format(amount);
        } catch {
          return hasFraction ? String(Math.round(amount * 100) / 100) : String(Math.round(amount));
        }
      };

      // Match Liquid money filters: use shop symbol, not Intl currency mode (avoids "US$" vs "$").
      if (shopSymbol) {
        return `${shopSymbol}${formatDecimal()}`;
      }

      const code = (this.dataset.xfyCurrency || 'USD').trim() || 'USD';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: hasFraction ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return formatDecimal();
      }
    }

    /** Keep DOM aligned with bundle-selector pricing row: outer price span + inner span.money (CSS targets nested span). */
    #xfySetPriceRowMoneySpan(outerEl, text) {
      if (!outerEl) return;
      let money = outerEl.querySelector(':scope > .money');
      if (!money) {
        money = document.createElement('span');
        money.className = 'money';
        outerEl.replaceChildren(money);
      }
      money.textContent = text;
    }

    #xfyClearPriceRowMoneySpan(outerEl) {
      if (!outerEl) return;
      const money = outerEl.querySelector(':scope > .money');
      if (money) money.textContent = '';
      else outerEl.textContent = '';
    }

    /** Sum of each selected variant’s current price (Shopify cents) — same as cart “Sale price”, not compare_at. */
    #xfyComputePricingTotals() {
      let sumSell = 0;
      for (const cat of this.xfyCategories) {
        const key = String(cat.key);
        const st = this.xfyState[key];
        if (!st) continue;
        const product = this.#xfyProductForCategory(cat, st.selectedProductId);
        if (!product) continue;
        const v = this.#xfyVariantForPricing(product, st.selectedOptions || {});
        if (!v) continue;
        const price = Math.round(Number(v.price));
        sumSell += Number.isFinite(price) && price > 0 ? price : 0;
      }
      return { sumSell };
    }

    #xfyUpdateBundlePricing() {
      const discount = Math.max(0, Math.min(100, Number(this.dataset.bundleDiscountPercentage) || 0));
      const slotCount = Math.max(1, Number(this.dataset.bundleSlotCount) || this.xfyCategories.length || 1);
      const { sumSell } = this.#xfyComputePricingTotals();
      const saleCents = discount > 0 ? Math.round((sumSell * (100 - discount)) / 100) : sumSell;
      const unitCents = Math.round(saleCents / slotCount);

      const row = this.querySelector('[data-xfy-bundle-pricing-row]');
      const compareEl = row
        ? row.querySelector('[data-xfy-price-compare]')
        : this.querySelector('[data-xfy-price-compare]');
      const saleEl = row
        ? row.querySelector('[data-xfy-price-sale]')
        : this.querySelector('[data-xfy-price-sale]');
      const unitEl = this.querySelector('[data-xfy-unit-each]');

      if (compareEl) {
        if (discount > 0 && sumSell > 0 && sumSell > saleCents) {
          this.#xfySetPriceRowMoneySpan(compareEl, this.#xfyFormatMoneyCents(sumSell));
          compareEl.removeAttribute('hidden');
          compareEl.hidden = false;
        } else {
          this.#xfyClearPriceRowMoneySpan(compareEl);
          compareEl.hidden = true;
        }
      }
      if (saleEl) this.#xfySetPriceRowMoneySpan(saleEl, this.#xfyFormatMoneyCents(saleCents));
      if (unitEl) unitEl.textContent = this.#xfyFormatMoneyCents(unitCents);

      const afterpay = this.querySelector('.bundle-selector__afterpay[data-afterpay-amount]');
      if (afterpay) {
        const nextAmount = String(saleCents);
        const prevAmount = afterpay.getAttribute('data-afterpay-amount');
        afterpay.setAttribute('data-afterpay-amount', nextAmount);
        if (prevAmount !== nextAmount) {
          this.dispatchEvent(
            new CustomEvent('xfy-bundle-pricing-changed', {
              bubbles: true,
              detail: { afterpayAmount: saleCents },
            })
          );
        }
      }
    }

    #xfyPrimeDefaultOptions(key) {
      const cat = this.xfyCategories.find((c) => String(c.key) === String(key));
      if (!cat) return;
      const st = this.xfyState[String(key)];
      if (!st) return;
      const product = this.#xfyProductForCategory(cat, st.selectedProductId);
      if (!product) return;
      const owv = product.options_with_values || [];
      const selectedOptions = { ...st.selectedOptions };
      owv.forEach((option, oi) => {
        const optKey = option.name.toLowerCase();
        if (selectedOptions[optKey]) return;
        const found = option.values.find((value) => this.#xfyOptionValueAvailable(product, oi, value, selectedOptions));
        if (found) selectedOptions[optKey] = found;
      });
      st.selectedOptions = selectedOptions;
    }

    #xfyRenderOptionPanel(key) {
      const container = this.querySelector(`[data-xfy-option-selector="${key}"]`);
      if (!container) return;
      const cat = this.xfyCategories.find((c) => String(c.key) === String(key));
      const st = this.xfyState[String(key)];
      if (!cat || !st) return;

      const product = this.#xfyProductForCategory(cat, st.selectedProductId);
      if (!product) {
        container.innerHTML = '';
        return;
      }

      this.#xfyPrimeDefaultOptions(key);

      const owv = product.options_with_values || [];
      const selectedOptions = st.selectedOptions;
      const showSizeGuide = this.dataset.showSizeGuide === 'true';
      const liquidSizeRowInitial = container.dataset.liquidSizeRow === 'true';
      const liquidMeta = this.querySelector(`[data-xfy-liquid-size-meta="${key}"]`);
      const hasSizeOption = owv.some((o) => String(o.name).toLowerCase().includes('size'));

      if (liquidMeta) {
        liquidMeta.style.display = hasSizeOption ? '' : 'none';
      }

      let html = '';

      owv.forEach((option, oi) => {
        const optKey = option.name.toLowerCase();
        const isSizeOption = optKey.includes('size');
        const useLiquidSizeMeta =
          isSizeOption && liquidSizeRowInitial && hasSizeOption && Boolean(liquidMeta);

        html +=
          '<fieldset class="bundle-selector__size-fieldset variant-option variant-option--buttons variant-option--equal-width-buttons bundle-xfy-option-fieldset">';
        if (!useLiquidSizeMeta) {
          html += '<div class="bundle-selector__size-meta">';
          html += `<span class="bundle-selector__size-label">${this.#xfyEscapeHtml(option.name)}:</span>`;
          if (isSizeOption && showSizeGuide) {
            html +=
              '<button type="button" class="bundle-selector__size-guide" data-action="size-guide">Size Guide</button>';
          }
          html += '</div>';
        }
        html += '<div class="bundle-selector__size-options">';
        option.values.forEach((value, vi) => {
          const disabled = !this.#xfyOptionValueAvailable(product, oi, value, selectedOptions);
          const checked = selectedOptions[optKey] === value;
          const id = `xfy-${key}-${oi}-${vi}`;
          html += `<label class="variant-option__button-label${disabled ? ' unavailable' : ''}">`;
          html += `<input type="radio" class="bundle-xfy-option-input" name="xfy-opt-${key}-${oi}" id="${this.#xfyEscapeAttr(id)}" data-xfy-option data-category-key="${this.#xfyEscapeAttr(String(key))}" data-option-key="${this.#xfyEscapeAttr(optKey)}" value="${this.#xfyEscapeAttr(value)}" ${disabled ? 'disabled' : ''} ${checked ? 'checked' : ''} />`;
          html += `<span class="variant-option__button-label__text">${this.#xfyEscapeHtml(value)}</span>`;
          html += '</label>';
        });
        html += '</div></fieldset>';
      });

      container.innerHTML = html;
    }

    #xfyUpdateColorLabels() {
      this.xfyCategories.forEach((cat) => {
        const key = String(cat.key);
        const input = this.querySelector(`input[name="xfy-color-${key}"]:checked`);
        const label = input?.dataset?.colorLabel || '';
        const el = this.querySelector(`[data-xfy-active-color-label="${key}"]`);
        if (el) el.textContent = label;
      });
    }

    #xfySyncCategoryImages() {
      this.xfyCategories.forEach((cat) => {
        const key = String(cat.key);
        const st = this.xfyState[key];
        const product = this.#xfyProductForCategory(cat, st?.selectedProductId);
        const wrap = this.querySelector(`[data-xfy-category-image="${key}"]`);
        if (!wrap || !product?.media?.[0]) return;
        const src = product.media[0].src;
        const alt = product.media[0].alt || product.title || '';
        wrap.innerHTML = `<img src="${this.#xfyEscapeAttr(src)}" alt="${this.#xfyEscapeAttr(alt)}" width="300" height="375" loading="lazy" />`;
      });
    }

    #xfyUpdateMediaPreviewForKey(key) {
      if (!key) return;
      const product = this.#xfySelectedProductForKey(key);
      if (!product) return;
      const st = this.xfyState[String(key)];
      const variant = this.#xfyPartialVariantForGallery(product, st.selectedOptions);
      let sizeValue = '';
      const sidx = Number(product.size_option_index);
      if (variant && sidx >= 0 && variant.options[sidx]) {
        sizeValue = variant.options[sidx];
      }
      this.#updateMediaGallery(Number(product.id), sizeValue);
    }

    #xfyOnFormChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.matches('[data-xfy-product-select]')) {
        const key = target.dataset.categoryKey;
        if (key == null) return;
        if (!this.xfyState[key]) return;
        const st = this.xfyState[key];
        const prevSelected = { ...st.selectedOptions };
        st.selectedProductId = Number(target.value);
        st.selectedOptions = Object.create(null);

        const cat = this.xfyCategories.find((c) => String(c.key) === String(key));
        const product = cat ? this.#xfyProductForCategory(cat, st.selectedProductId) : null;
        if (product) {
          const owv = product.options_with_values || [];
          owv.forEach((option, oi) => {
            const optKey = option.name.toLowerCase();
            const prevVal = prevSelected[optKey];
            if (prevVal == null || prevVal === '') return;
            const normalizedPrev = String(prevVal).trim();
            const matchedValue = option.values.find((v) => String(v).trim() === normalizedPrev);
            if (!matchedValue) return;
            if (this.#xfyOptionValueAvailable(product, oi, matchedValue, st.selectedOptions)) {
              st.selectedOptions[optKey] = matchedValue;
            }
          });
        }
        this.#xfyPrimeDefaultOptions(key);

        this.#xfyRenderOptionPanel(key);
        this.#xfyUpdateColorLabels();
        this.#xfySyncCategoryImages();
        this.#xfyUpdateMediaPreviewForKey(key);
        this.#xfyUpdateBundlePricing();
        this.#xfySyncUi();
        return;
      }

      if (target.matches('[data-xfy-option]')) {
        const key = target.dataset.categoryKey;
        const optKey = target.dataset.optionKey;
        if (key == null || !optKey || !this.xfyState[key]) return;
        if (target.checked) {
          this.xfyState[key].selectedOptions[optKey] = target.value;
        }
        this.#xfyRenderOptionPanel(key);
        this.#xfyUpdateMediaPreviewForKey(key);
        this.#xfyUpdateBundlePricing();
        this.#xfySyncUi();
      }
    }

    #xfyAllVariantsReady() {
      return this.xfyCategories.every((cat) => {
        const key = String(cat.key);
        const st = this.xfyState[key];
        const product = this.#xfyProductForCategory(cat, st?.selectedProductId);
        const v = this.#xfyMatchingVariant(product, st?.selectedOptions || {});
        return Boolean(v);
      });
    }

    #xfySyncUi() {
      this.#setStatus('');
      const stickyBtn = this.#getStickyButton();

      if (this.#xfyAllVariantsReady()) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.textContent = 'Add To Cart';
        if (stickyBtn) {
          stickyBtn.disabled = false;
          stickyBtn.textContent = 'Add To Cart';
        }
        return;
      }

      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = 'Select options and colors';
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = 'Select options and colors';
      }

      const hasCompleteSelection = this.xfyCategories.every((cat) => {
        const key = String(cat.key);
        const product = this.#xfySelectedProductForKey(key);
        const st = this.xfyState[key];
        const owv = product?.options_with_values || [];
        if (!owv.length) return true;
        return owv.every((opt) => st.selectedOptions[opt.name.toLowerCase()]);
      });

      if (hasCompleteSelection) {
        this.#setStatus('Selected options are unavailable for one or more items.', true);
      }
    }

    async #xfyOnSubmit(event) {
      event.preventDefault();
      if (!this.addToCartButton || this.addToCartButton.disabled) return;
      if (!this.#xfyAllVariantsReady()) return;

      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = 'Adding...';
      const stickyBtn = this.#getStickyButton();
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = 'Adding...';
      }
      this.#setStatus('');

      try {
        const sections = Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => element.dataset.sectionId)
          .filter(Boolean);

        const bundleId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/x/g, () =>
                Math.floor(Math.random() * 16).toString(16)
              );
        const bundleDiscountPercentage = this.dataset.bundleDiscountPercentage || '';
        const bundleTitle = this.dataset.bundleTitle || '';
        const slotCount = String(this.dataset.bundleSlotCount || this.xfyCategories.length || '1');

        const bundleProperties = {
          _bundle_id: bundleId,
          _bundle_size: slotCount,
          _bundle_discount_percentage: String(bundleDiscountPercentage),
          _bundle_title: bundleTitle,
        };

        const items = this.xfyCategories.map((cat) => {
          const key = String(cat.key);
          const st = this.xfyState[key];
          const product = this.#xfyProductForCategory(cat, st.selectedProductId);
          const variant = this.#xfyMatchingVariant(product, st.selectedOptions || {});
          return {
            id: Number(variant.id),
            quantity: 1,
            properties: bundleProperties,
          };
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add bundle.');
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: payload,
              sourceId: `bundle-xfy-${this.dataset.sectionId || 'product'}`,
              data: {
                source: 'product-form-component',
                itemCount: items.length,
                sections: payload.sections || {},
              },
            },
          })
        );

        this.addToCartButton.textContent = 'Added';
        if (stickyBtn) stickyBtn.textContent = 'Added';
        window.setTimeout(() => this.#xfySyncUi(), 800);
      } catch (error) {
        this.#setStatus(error.message || 'Something went wrong. Please try again.', true);
        this.#xfySyncUi();
      }
    }
  }

  if (!customElements.get('product-bundler-xfy')) {
    customElements.define('product-bundler-xfy', ProductBundlerXfy);
  }
})();
